import { format, isValid } from "date-fns";
import { Calendar } from "lucide-react";
import React, { useMemo, useRef } from "react";

import { DEFAULT_DATE_PATTERN } from "../../constants";
import { parseCalendarDate } from "../../utils/dateUtils";

const LAYOUT_CLASSES = new Set(["flex-1", "flex-auto", "flex-initial", "flex-none", "w-full"]);

function splitClassNames(className?: string): { wrapper: string; input: string } {
  if (!className) return { wrapper: "", input: "" };
  const wrapperParts: string[] = [];
  const inputParts: string[] = [];
  for (const cls of className.split(/\s+/)) {
    if (LAYOUT_CLASSES.has(cls) || cls.startsWith("w-") || cls.startsWith("sm:w-")) {
      wrapperParts.push(cls);
    } else {
      inputParts.push(cls);
    }
  }
  return { wrapper: wrapperParts.join(" "), input: inputParts.join(" ") };
}

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  id?: string;
}

/**
 * A date input that displays dates using the app's standard format
 * regardless of browser locale, while still leveraging the native calendar picker.
 *
 * `value` and `onChange` use the standard YYYY-MM-DD format.
 */
export const DateInput: React.FC<DateInputProps> = ({ value, onChange, className, id }) => {
  const dateRef = useRef<HTMLInputElement>(null);
  const { wrapper, input } = useMemo(() => splitClassNames(className), [className]);

  const displayValue = useMemo(() => {
    if (!value) return "";
    const date = parseCalendarDate(value);
    return date && isValid(date) ? format(date, DEFAULT_DATE_PATTERN) : value;
  }, [value]);

  const openPicker = () => {
    try {
      dateRef.current?.showPicker();
    } catch {
      dateRef.current?.focus();
      dateRef.current?.click();
    }
  };

  return (
    <div className={`relative ${wrapper}`}>
      <input
        id={id}
        type="text"
        readOnly
        value={displayValue}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        placeholder={DEFAULT_DATE_PATTERN.toUpperCase()}
        className={`w-full ${input}`}
        role="button"
        aria-haspopup="dialog"
      />
      <input
        ref={dateRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pointer-events-none invisible absolute inset-0 h-0 w-0"
        tabIndex={-1}
        aria-hidden="true"
      />
      <Calendar
        size={16}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
      />
    </div>
  );
};
